import { createContext, ReactElement, ReactNode, useContext, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'

import { useForm, UseFormReturn } from '../../../../../hooks'
import { useInvoiceEditorDocumentType } from '../hooks/useInvoiceEditorDocumentType'
import { useInvoiceIssueDateLimits } from '../hooks/useInvoiceIssueDateLimits'
import { InvoiceColumnVisibilityFormSchema } from '../types/invoiceColumnVisibilityForm/invoiceColumnVisibilityFormSchema'
import { InvoiceFormSchema } from '../types/invoiceFormSchema'
import { RecurringInvoiceFormSchema } from '../types/recurringInvoiceForm/recurringInvoiceFormSchema'
import { getInvoiceFormDefaultValues } from '../utils/invoiceEditorForm/getInvoiceFormDefaultValues'
import { getInvoiceFormSchema } from '../utils/invoiceEditorForm/getInvoiceFormSchema'
import { getInvoiceColumnVisibilityFormDefaultValues } from '../utils/invoiceLinesColumnsVisibilityForm/getInvoiceColumnVisibilityFormDefaultValues'
import { getInvoiceColumnVisibilityFormSchema } from '../utils/invoiceLinesColumnsVisibilityForm/getInvoiceColumnVisibilityFormSchema'
import { getRecurringInvoiceFormDefaultValues } from '../utils/recurringInvoice/getRecurringInvoiceFormDefaultValues'
import { getRecurringInvoiceFormSchema } from '../utils/recurringInvoice/getRecurringInvoiceFormSchema'

interface ContextState {
  invoiceEditorForm: UseFormReturn<InvoiceFormSchema>
  invoiceLinesColumnsVisibilityForm: UseFormReturn<InvoiceColumnVisibilityFormSchema>
  recurringInvoiceForm: UseFormReturn<RecurringInvoiceFormSchema>
  setInvoiceEditorFormSchema: (schema: InvoiceFormSchema) => void
}

const InvoiceEditorFormsContext = createContext<ContextState | undefined>(undefined)

interface InvoiceEditorFormsProps {
  children?: ReactNode
}

// This context provides forms used in the invoice editor.
// We need to be able to clearly refer to specific forms, as useFormContext will always
// bind to the nearest form provider (<Form>), which might not always be what we want
// when e.g. trying to observe a form value (using watch) and being bound to the wrong
// form. And we need to be able to nest form providers, thus the approach of not using
// useFormContext, but rather provide specific forms from one source context.
export const InvoiceEditorFormsProvider = ({ children }: InvoiceEditorFormsProps): ReactElement => {
  const { t } = useTranslation()
  const { organization } = useUserOrganization()
  const { isCreditNote, isRecurringInvoice } = useInvoiceEditorDocumentType()

  const { fiscalLockingDate, latestInvoiceIssueDate } = useInvoiceIssueDateLimits()
  const [invoiceEditorFormSchema, setInvoiceEditorFormSchema] = useState(
    getInvoiceFormSchema({ fiscalLockingDate, isCreditNote, latestInvoiceIssueDate, t }),
  )

  const invoiceEditorForm = useForm<InvoiceFormSchema>({
    defaultValues: getInvoiceFormDefaultValues({ isRecurringInvoice }),
    validationSchema: invoiceEditorFormSchema,
  })

  const invoiceLinesColumnsVisibilityForm = useForm<InvoiceColumnVisibilityFormSchema>({
    defaultValues: getInvoiceColumnVisibilityFormDefaultValues(),
    validationSchema: getInvoiceColumnVisibilityFormSchema(),
  })

  const recurringInvoiceForm = useForm<RecurringInvoiceFormSchema>({
    defaultValues: getRecurringInvoiceFormDefaultValues({ t, organizationName: organization?.name || '' }),
    validationSchema: getRecurringInvoiceFormSchema({ t }),
  })

  const contextValue = useMemo(
    () => ({
      invoiceEditorForm,
      invoiceLinesColumnsVisibilityForm,
      recurringInvoiceForm,
      setInvoiceEditorFormSchema,
    }),
    [invoiceEditorForm, invoiceLinesColumnsVisibilityForm, recurringInvoiceForm, setInvoiceEditorFormSchema],
  )

  return <InvoiceEditorFormsContext.Provider value={contextValue}>{children}</InvoiceEditorFormsContext.Provider>
}

export const useInvoiceEditorForms = () => {
  const context = useContext(InvoiceEditorFormsContext)

  if (!context) {
    throw new Error('InvoiceEditorFormsProvider is missing in the module!')
  }

  return context
}
